import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useTranslation } from '../i18n';
import SearchBar from '../components/SearchBar';
import BlogSection from '../components/BlogSection';
import './Home.css';

const Home = () => {
  const { t, language } = useTranslation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [greeting, setGreeting] = useState('');

  const business = process.env.REACT_APP_SITE_NAME;
  const first_name = business ? business.split(' ')[0] : 'Seek';
  const last_name = business ? business.split(' ').slice(1).join(' ') : 'Webs';

  const localeMap = {
    pt: 'pt-BR',
    en: 'en-US',
    es: 'es-ES'
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    const hour = new Date().getHours();
    if (hour < 12) setGreeting(t('greeting.morning'));
    else if (hour < 18) setGreeting(t('greeting.afternoon'));
    else setGreeting(t('greeting.evening'));

    return () => clearInterval(timer);
  }, [t]);

  const features = [
    {
      icon: '🔍',
      title: t('home.features.smartSearch.title'),
      description: t('home.features.smartSearch.description')
    },
    {
      icon: '🔒',
      title: t('home.features.privacy.title'),
      description: t('home.features.privacy.description')
    },
    {
      icon: '⚡',
      title: t('home.features.speed.title'),
      description: t('home.features.speed.description')
    },
    {
      icon: '🌐',
      title: t('home.features.global.title'),
      description: t('home.features.global.description')
    }
  ];

  return (
    <>
      <Helmet>
        <title>{business} - {t('home.title')}</title>
        <meta name="description" content={t('home.metaDescription', { business: business || 'SeekWebs' })} />
      </Helmet>

      <div className="home-container">
        <motion.header
          className="home-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="time-greeting">
            <h2 className="greeting">{greeting}</h2>
            <p className="time">{currentTime.toLocaleTimeString(localeMap[language] || 'pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
        </motion.header>

        <motion.main
          className="home-main"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <motion.div
            className="logo-container"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              delay: 0.3
            }}
          >
            <div className="logo">
              <span className="logo-text">{first_name}</span>
              <span className="logo-accent">{last_name}</span>
            </div>
            <motion.p
              className="tagline"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {t('home.title')}
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <SearchBar />
          </motion.div>

          <motion.div
            className="features-grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                whileHover={{
                  scale: 1.05,
                  transition: { duration: 0.2 }
                }}
              >
                <span className="feature-icon">{feature.icon}</span>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="quick-links"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
          >
            <h3>{t('home.trending.title')}</h3>
            <div className="trending-topics">
              <motion.span
                className="topic"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('home.trending.topics.technology')}
              </motion.span>
              <motion.span
                className="topic"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('home.trending.topics.news')}
              </motion.span>
              <motion.span
                className="topic"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('home.trending.topics.sports')}
              </motion.span>
              <motion.span
                className="topic"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('home.trending.topics.entertainment')}
              </motion.span>
            </div>
          </motion.div>

          <BlogSection />
        </motion.main>

        <AnimatePresence>
          <motion.div
            className="background-gradient"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
          />
        </AnimatePresence>
      </div>
    </>
  );
};

export default Home;
